import React, { useEffect, useState } from 'react';
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';

import CustomAppBar from './CustomAppBar';
import CustomDrawer from './CustomDrawer';
import { 
  FaCalendarAlt, 
  FaClock, 
  FaFish, 
  FaHeart, 
  FaMars, 
  FaVenus, 
  FaTimes, 
  FaCheckCircle, 
  FaExclamationCircle, 
  FaSeedling, 
  FaHome,
  FaShoppingCart,
  FaMoneyBillWave,
  FaInfoCircle,
  FaTruck,
  FaBoxOpen,
  FaPhone,
  FaComment,
  FaChevronRight,
  FaTags,
  FaCheck,
  FaSpinner
} from 'react-icons/fa';

const OrdersPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [cancellingOrder, setCancellingOrder] = useState(null);
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const navigate = useNavigate();

  // Helper function to format date
  const formatDate = (timestamp) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('ar-DZ', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format time
  const formatTime = (timestamp) => {
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleTimeString('ar-DZ', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  // Helper function to determine item type
  const getItemType = (item) => {
    if (item.type === "plant") return "plant";
    if (item.type === "aquarium") return "aquarium";
    if (item.guppyName || item.coupleCount !== undefined || item.couplecount !== undefined) return "guppy";
    return "unknown";
  };

  // Helper function to calculate order statistics
  const calculateOrderStats = (order) => {
    const totalItems = order.items?.length || 1;
    const hasDiscount = order.items?.some(item => 
      (item.coupleCount >= 5 || item.couplecount >= 5) ||
      (item.quantity >= 5)
    ) || false;
    
    return {
      totalItems,
      hasDiscount,
      originalPrice: order.originalPrice || order.totalPrice,
      finalPrice: order.totalPrice
    };
  };

const fetchUserOrders = async () => {
  try {
    const orderIdsJson = localStorage.getItem('orderIds') ?? '[]';
    const orderIds = JSON.parse(orderIdsJson);

    const ordersData = [];
    for (let orderId of orderIds) {
      const orderDoc = await getDoc(doc(db, 'orders', orderId));
      if (orderDoc.exists() && orderDoc.data().status !== 'CancelledByClient') {
        ordersData.push({ id: orderDoc.id, ...orderDoc.data() });
      }
    }

    // Sort orders by date (newest first)
    ordersData.sort((a, b) => {
      const dateA = a.orderDate?.toDate?.() || 0;
      const dateB = b.orderDate?.toDate?.() || 0;
      return dateB - dateA;
    });
    
    // Update localStorage to remove any invalid IDs
    const validOrderIds = ordersData.map(order => order.id);
    localStorage.setItem('orderIds', JSON.stringify(validOrderIds));
    
    // Update state with the fetched orders
    setOrders(ordersData);
    setLoading(false);
  } catch (e) {
    console.error('Error fetching orders:', e);
    setLoading(false);
  }
};
  const cancelOrder = async (orderId) => {
    setCancellingOrder(orderId);
    try {
      const orderRef = doc(db, 'orders', orderId);
      await updateDoc(orderRef, { status: 'CancelledByClient' });

      const orderIdsJson = localStorage.getItem('orderIds') ?? '[]';
      const orderIds = JSON.parse(orderIdsJson);
      const updatedOrderIds = orderIds.filter((id) => id !== orderId);
      localStorage.setItem('orderIds', JSON.stringify(updatedOrderIds));

      setOrders(orders.filter((order) => order.id !== orderId));
      
      // Success notification
      setTimeout(() => {
        alert('تم إلغاء الطلب بنجاح! ✅');
      }, 300);
    } catch (e) {
      console.error('Error canceling order:', e);
      alert('فشل إلغاء الطلب. من فضلك حاول مرة أخرى. ❌');
    } finally {
      setCancellingOrder(null);
    }
  };

  const getStatusInfo = (status) => {
    switch (status) {
      case 'Pending':
        return {
          text: 'قيد الانتظار',
          color: '#fbbf24',
          bgColor: 'rgba(251, 191, 36, 0.1)',
          icon: <FaClock />,
          step: 1,
          description: 'جاري مراجعة طلبك'
        };
      case 'Confirmed':
        return {
          text: 'تم التأكيد',
          color: '#10b981',
          bgColor: 'rgba(16, 185, 129, 0.1)',
          icon: <FaCheckCircle />,
          step: 2,
          description: 'تم تأكيد طلبك بنجاح'
        };
      case 'Shipped':
        return {
          text: 'تم الشحن',
          color: '#3b82f6',
          bgColor: 'rgba(59, 130, 246, 0.1)',
          icon: <FaTruck />,
          step: 3,
          description: 'طلبك في الطريق إليك'
        };
      case 'Delivered':
        return {
          text: 'تم التسليم',
          color: '#10b981',
          bgColor: 'rgba(16, 185, 129, 0.1)',
          icon: <FaBoxOpen />,
          step: 4,
          description: 'تم تسليم طلبك بنجاح'
        };
      case 'CancelledByClient':
        return {
          text: 'تم الإلغاء',
          color: '#ef4444',
          bgColor: 'rgba(239, 68, 68, 0.1)',
          icon: <FaTimes />,
          step: 0,
          description: 'تم إلغاء الطلب'
        };
      default:
        return {
          text: status,
          color: '#94a3b8',
          bgColor: 'rgba(148, 163, 184, 0.1)',
          icon: <FaExclamationCircle />,
          step: 0,
          description: status
        };
    }
  };

  // Toggle order expansion
  const toggleOrderExpansion = (orderId) => {
    const newExpanded = new Set(expandedOrders);
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId);
    } else {
      newExpanded.add(orderId);
    }
    setExpandedOrders(newExpanded);
  };

  // Render order timeline
  const renderOrderTimeline = (order) => {
    const statusInfo = getStatusInfo(order.status);
    const steps = [
      { id: 'Pending', label: 'قيد الانتظار', icon: <FaClock /> },
      { id: 'Confirmed', label: 'تم التأكيد', icon: <FaCheckCircle /> },
      { id: 'Shipped', label: 'تم الشحن', icon: <FaTruck /> },
      { id: 'Delivered', label: 'تم التسليم', icon: <FaBoxOpen /> }
    ];

    if (order.status === 'CancelledByClient') {
      return null;
    }

    return (
      <div style={{
        padding: '20px',
        background: 'rgba(15, 23, 42, 0.5)',
        borderRadius: '16px',
        marginTop: '20px',
        border: '1px solid rgba(51, 65, 85, 0.5)'
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '20px'
        }}>
          <FaInfoCircle style={{ color: '#06b6d4', fontSize: '1.1rem' }} />
          <h4 style={{
            color: '#cbd5e1',
            fontSize: '1rem',
            fontWeight: '600',
            margin: 0
          }}>حالة الطلب</h4>
        </div>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
          paddingBottom: '10px'
        }}>
          {steps.map((step, index) => {
            const stepStatus = getStatusInfo(step.id);
            const isActive = statusInfo.step >= stepStatus.step;
            const isCurrent = statusInfo.step === stepStatus.step;
            
            return (
              <div key={step.id} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                flex: 1,
                position: 'relative',
                zIndex: 2
              }}>
                <div style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  background: isActive 
                    ? `linear-gradient(135deg, ${stepStatus.color}, ${stepStatus.color}80)`
                    : 'rgba(51, 65, 85, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: isActive ? 'white' : '#64748b',
                  fontSize: '1.1rem',
                  border: isCurrent ? `3px solid ${stepStatus.color}` : '3px solid transparent',
                  boxShadow: isCurrent ? `0 0 20px ${stepStatus.color}40` : 'none',
                  transition: 'all 0.3s ease',
                  marginBottom: '8px',
                  position: 'relative'
                }}>
                  {isActive && !isCurrent && <FaCheck size={16} />}
                  {isCurrent && stepStatus.icon}
                  {!isActive && stepStatus.icon}
                </div>
                <span style={{
                  fontSize: '0.75rem',
                  color: isActive ? '#cbd5e1' : '#64748b',
                  textAlign: 'center',
                  fontWeight: isCurrent ? '600' : '400'
                }}>
                  {step.label}
                </span>
              </div>
            );
          })}
          {/* Connecting line */}
          <div style={{
            position: 'absolute',
            top: '24px',
            left: '24px',
            right: '24px',
            height: '3px',
            background: 'rgba(51, 65, 85, 0.5)',
            zIndex: 1
          }}>
            <div style={{
              width: `${(statusInfo.step / 4) * 100}%`,
              height: '100%',
              background: `linear-gradient(90deg, ${statusInfo.color}, ${statusInfo.color}80)`,
              transition: 'width 0.5s ease',
              borderRadius: '2px'
            }}></div>
          </div>
        </div>
        {statusInfo.description && (
          <div style={{
            marginTop: '12px',
            padding: '12px 16px',
            background: statusInfo.bgColor,
            borderRadius: '12px',
            border: `1px solid ${statusInfo.color}40`,
            color: statusInfo.color,
            fontSize: '0.9rem',
            textAlign: 'center',
            fontWeight: '500'
          }}>
            {statusInfo.description}
          </div>
        )}
      </div>
    );
  };

  // Render item details based on type
  const renderItemDetails = (item) => {
    const itemType = getItemType(item);

    switch (itemType) {
      case "plant":
        return (
          <div style={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '16px',
            border: '1px solid rgba(51, 65, 85, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(51, 65, 85, 0.5)'
            }}>
              <FaSeedling style={{ color: '#10b981', fontSize: '1.25rem' }} />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#cbd5e1',
                margin: '0'
              }}>
                {item.name}
              </h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <FaSeedling style={{ color: '#10b981' }} />
                <div>
                  <div style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: '600' }}>النوع</div>
                  <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.plantType || "نبات"}</div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <FaHeart style={{ color: '#3b82f6' }} />
                <div>
                  <div style={{ color: '#3b82f6', fontSize: '0.8rem', fontWeight: '600' }}>الكمية</div>
                  <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.quantity}</div>
                </div>
              </div>

              {item.price && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(251, 191, 36, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(251, 191, 36, 0.2)'
                }}>
                  <FaMoneyBillWave style={{ color: '#fbbf24' }} />
                  <div>
                    <div style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: '600' }}>السعر الوحدة</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.price} DZD</div>
                  </div>
                </div>
              )}

              {item.care && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(236, 72, 153, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(236, 72, 153, 0.2)'
                }}>
                  <FaInfoCircle style={{ color: '#ec4899' }} />
                  <div>
                    <div style={{ color: '#ec4899', fontSize: '0.8rem', fontWeight: '600' }}>العناية</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.care}</div>
                  </div>
                </div>
              )}

              {item.tags && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(139, 92, 246, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(139, 92, 246, 0.2)'
                }}>
                  <FaSeedling style={{ color: '#8b5cf6' }} />
                  <div>
                    <div style={{ color: '#8b5cf6', fontSize: '0.8rem', fontWeight: '600' }}>عدد الفروع</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.tags}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "aquarium":
        return (
          <div style={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '16px',
            border: '1px solid rgba(51, 65, 85, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(51, 65, 85, 0.5)'
            }}>
              <FaHome style={{ color: '#10b981', fontSize: '1.25rem' }} />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#cbd5e1',
                margin: '0'
              }}>
                {item.name}
              </h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <FaHome style={{ color: '#10b981' }} />
                <div>
                  <div style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: '600' }}>الكمية</div>
                  <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.quantity}</div>
                </div>
              </div>

              {item.price && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(251, 191, 36, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(251, 191, 36, 0.2)'
                }}>
                  <FaMoneyBillWave style={{ color: '#fbbf24' }} />
                  <div>
                    <div style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: '600' }}>السعر الوحدة</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.price} DZD</div>
                  </div>
                </div>
              )}

              {item.size && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                  <FaInfoCircle style={{ color: '#3b82f6' }} />
                  <div>
                    <div style={{ color: '#3b82f6', fontSize: '0.8rem', fontWeight: '600' }}>الحجم</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.size}</div>
                  </div>
                </div>
              )}

              {item.material && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(236, 72, 153, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(236, 72, 153, 0.2)'
                }}>
                  <FaHome style={{ color: '#ec4899' }} />
                  <div>
                    <div style={{ color: '#ec4899', fontSize: '0.8rem', fontWeight: '600' }}>المادة</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.material}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case "guppy":
        return (
          <div style={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '16px',
            border: '1px solid rgba(51, 65, 85, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(51, 65, 85, 0.5)'
            }}>
              <FaFish style={{ color: '#10b981', fontSize: '1.25rem' }} />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#cbd5e1',
                margin: '0'
              }}>
                {item.guppyName || item.name}
              </h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(16, 185, 129, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(16, 185, 129, 0.2)'
              }}>
                <FaHeart style={{ color: '#10b981' }} />
                <div>
                  <div style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: '600' }}>الأزواج</div>
                  <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>
                    {item.coupleCount || item.couplecount || 0}
                    {(item.coupleCount >= 5 || item.couplecount >= 5) && (
                      <span style={{ color: '#10b981', fontSize: '0.7rem', marginRight: '4px' }}>(-10%)</span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(59, 130, 246, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(59, 130, 246, 0.2)'
              }}>
                <FaMars style={{ color: '#3b82f6' }} />
                <div>
                  <div style={{ color: '#3b82f6', fontSize: '0.8rem', fontWeight: '600' }}>الذكور</div>
                  <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.maleCount || item.malecount || 0}</div>
                </div>
              </div>

              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                background: 'rgba(236, 72, 153, 0.1)',
                borderRadius: '12px',
                border: '1px solid rgba(236, 72, 153, 0.2)'
              }}>
                <FaVenus style={{ color: '#ec4899' }} />
                <div>
                  <div style={{ color: '#ec4899', fontSize: '0.8rem', fontWeight: '600' }}>الإناث</div>
                  <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.femaleCount || item.femalecount || 0}</div>
                </div>
              </div>

              {item.price && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(251, 191, 36, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(251, 191, 36, 0.2)'
                }}>
                  <FaMoneyBillWave style={{ color: '#fbbf24' }} />
                  <div>
                    <div style={{ color: '#fbbf24', fontSize: '0.8rem', fontWeight: '600' }}>السعر للزوج</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.price} DZD</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div style={{
            background: 'rgba(15, 23, 42, 0.5)',
            borderRadius: '16px',
            padding: '24px',
            marginBottom: '16px',
            border: '1px solid rgba(51, 65, 85, 0.5)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '16px',
              paddingBottom: '16px',
              borderBottom: '1px solid rgba(51, 65, 85, 0.5)'
            }}>
              <FaExclamationCircle style={{ color: '#10b981', fontSize: '1.25rem' }} />
              <h3 style={{
                fontSize: '1.25rem',
                fontWeight: 'bold',
                color: '#cbd5e1',
                margin: '0'
              }}>
                {item.name || 'عنصر غير محدد'}
              </h3>
            </div>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '16px'
            }}>
              {item.price && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(16, 185, 129, 0.2)'
                }}>
                  <FaMoneyBillWave style={{ color: '#10b981' }} />
                  <div>
                    <div style={{ color: '#10b981', fontSize: '0.8rem', fontWeight: '600' }}>السعر</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.price} DZD</div>
                  </div>
                </div>
              )}
              
              {item.quantity && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  padding: '12px 16px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '12px',
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}>
                  <FaInfoCircle style={{ color: '#3b82f6' }} />
                  <div>
                    <div style={{ color: '#3b82f6', fontSize: '0.8rem', fontWeight: '600' }}>الكمية</div>
                    <div style={{ color: '#cbd5e1', fontWeight: 'bold' }}>{item.quantity}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  // Render multiple items in an order
  const renderOrderItems = (order) => {
    if (!order.items || order.items.length === 0) {
      return <div style={{ color: '#94a3b8', textAlign: 'center', padding: '20px' }}>لا توجد عناصر في هذا الطلب</div>;
    }

    return (
      <div style={{ marginBottom: '24px' }}>
        {order.items.length > 1 && (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '16px',
            padding: '12px 16px',
            background: 'rgba(59, 130, 246, 0.1)',
            borderRadius: '12px',
            border: '1px solid rgba(59, 130, 246, 0.2)'
          }}>
            <FaShoppingCart style={{ color: '#3b82f6' }} />
            <span style={{ color: '#3b82f6', fontWeight: '600' }}>
              هذا الطلب يحتوي على {order.items.length} عناصر
            </span>
          </div>
        )}
        
        {order.items.map((item, index) => (
          <div key={index}>
            {renderItemDetails(item)}
          </div>
        ))}
      </div>
    );
  };

  useEffect(() => {
    fetchUserOrders();
  }, []);

  // Calculate total statistics
  const totalActiveOrders = orders.length;
  const totalSpent = orders.reduce((sum, order) => sum + (order.totalPrice || 0), 0);
  const pendingOrders = orders.filter(order => order.status === 'Pending').length;

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #0a0e1e 0%, #1a1f2e 25%, #0f172a 50%, #1a1f2e 75%, #0a0e1e 100%)',
      backgroundSize: '400% 400%',
      position: 'relative',
      width: '100%',
      overflowX: 'hidden',
      animation: 'gradientShift 15s ease infinite'
    }}>
      {/* Animated Particle Background */}
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 0,
        opacity: 0.15,
        pointerEvents: 'none'
      }}>
        {[...Array(50)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: `${Math.random() * 4 + 2}px`,
              height: `${Math.random() * 4 + 2}px`,
              background: `hsl(${200 + Math.random() * 60}, 70%, ${60 + Math.random() * 20}%)`,
              borderRadius: '50%',
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `float ${5 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`
            }}
          />
        ))}
      </div>

      {/* Background Pattern */}
      <div style={{
        position: 'absolute',
        inset: 0,
        opacity: 0.03,
        backgroundImage: `
          linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px),
          linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        zIndex: 0
      }}></div>

      <CustomAppBar onMenuClick={() => setDrawerOpen(true)} />
      
      <CustomDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCategorySelect={() => setDrawerOpen(false)}
      />

      {/* Header Section */}
      <div style={{
        position: 'relative',
        zIndex: 10,
        padding: '100px 16px 32px',
        maxWidth: '1200px',
        margin: '0 auto',
        textAlign: 'center'
      }}>
        <div style={{ marginBottom: '32px' }}>
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: 'bold',
            marginBottom: '16px',
            background: 'linear-gradient(135deg, #10b981, #06b6d4, #8b5cf6)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            lineHeight: '1.2',
            textShadow: '0 0 40px rgba(16, 185, 129, 0.3)',
            animation: 'fadeInDown 0.6s ease-out'
          }}>
            طلباتي 🛍️
          </h1>
          <p style={{
            color: '#94a3b8',
            fontSize: '1.1rem',
            marginBottom: '16px',
            animation: 'fadeInUp 0.6s ease-out 0.2s both'
          }}>
            تابع طلباتك وإدارة مشترياتك بسهولة
          </p>
          <div style={{
            width: '100px',
            height: '4px',
            background: 'linear-gradient(135deg, #10b981, #06b6d4)',
            margin: '0 auto',
            borderRadius: '2px',
            animation: 'fadeInUp 0.6s ease-out 0.4s both'
          }}></div>
        </div>
        
        {!loading && orders.length > 0 && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            maxWidth: '900px',
            margin: '32px auto 0'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(6, 182, 212, 0.1))',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.2)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(16, 185, 129, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(16, 185, 129, 0.2)';
            }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '8px',
                background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>📦</div>
              <div style={{ color: '#10b981', fontSize: '2rem', fontWeight: 'bold', marginBottom: '4px' }}>{totalActiveOrders}</div>
              <div style={{ color: '#10b981', fontSize: '0.95rem', fontWeight: '500' }}>طلب نشط</div>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(245, 158, 11, 0.1))',
              border: '1px solid rgba(251, 191, 36, 0.3)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(251, 191, 36, 0.2)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(251, 191, 36, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(251, 191, 36, 0.2)';
            }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '8px',
                background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>💰</div>
              <div style={{ color: '#fbbf24', fontSize: '2rem', fontWeight: 'bold', marginBottom: '4px' }}>{Math.round(totalSpent).toLocaleString()}</div>
              <div style={{ color: '#fbbf24', fontSize: '0.95rem', fontWeight: '500' }}>DZD إجمالي المبلغ</div>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(37, 99, 235, 0.1))',
              border: '1px solid rgba(59, 130, 246, 0.3)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center',
              boxShadow: '0 10px 30px rgba(59, 130, 246, 0.2)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 15px 40px rgba(59, 130, 246, 0.3)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(59, 130, 246, 0.2)';
            }}>
              <div style={{
                fontSize: '3rem',
                marginBottom: '8px',
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>⏳</div>
              <div style={{ color: '#3b82f6', fontSize: '2rem', fontWeight: 'bold', marginBottom: '4px' }}>{pendingOrders}</div>
              <div style={{ color: '#3b82f6', fontSize: '0.95rem', fontWeight: '500' }}>قيد الانتظار</div>
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px 32px', position: 'relative', zIndex: 10 }}>
        {loading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '500px',
            gap: '24px'
          }}>
            <div style={{
              width: '80px',
              height: '80px',
              border: '4px solid rgba(16, 185, 129, 0.3)',
              borderTop: '4px solid #10b981',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              boxShadow: '0 0 30px rgba(16, 185, 129, 0.3)'
            }}></div>
            <div style={{
              fontSize: '1.25rem',
              color: '#cbd5e1',
              fontWeight: '500',
              textAlign: 'center'
            }}>
              جاري تحميل طلباتك... 🔄
            </div>
            <div style={{
              fontSize: '0.9rem',
              color: '#94a3b8',
              textAlign: 'center',
              maxWidth: '300px'
            }}>
              يرجى الانتظار بينما نجلب أحدث معلومات طلباتك
            </div>
          </div>
        ) : orders.length === 0 ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            textAlign: 'center'
          }}>
            <div style={{
              background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(51, 65, 85, 0.9))',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(16, 185, 129, 0.2)',
              borderRadius: '24px',
              padding: '48px 32px',
              maxWidth: '400px',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '24px' }}>📦</div>
              <h3 style={{
                fontSize: '1.5rem',
                fontWeight: 'bold',
                color: '#cbd5e1',
                marginBottom: '12px'
              }}>لا توجد طلبات</h3>
              <p style={{
                color: '#94a3b8',
                lineHeight: '1.6',
                margin: '0 0 24px 0'
              }}>
                لم تقم بتقديم أي طلبات بعد. ابدأ التسوق واستكشف مجموعتنا المتنوعة من أسماك الزينة والنباتات!
              </p>
              <div style={{
                display: 'flex',
                gap: '12px',
                justifyContent: 'center',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={() => navigate('/')}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                    color: 'white',
                    padding: '14px 28px',
                    borderRadius: '25px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    border: 'none',
                    fontSize: '1rem',
                    boxShadow: '0 10px 25px rgba(16, 185, 129, 0.3)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 15px 35px rgba(16, 185, 129, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 10px 25px rgba(16, 185, 129, 0.3)';
                  }}
                >
                  ابدأ التسوق الآن! 🐠
                  <FaChevronRight size={14} />
                </button>
                <button
                  onClick={() => navigate('/chat')}
                  style={{
                    background: 'rgba(59, 130, 246, 0.2)',
                    color: '#60a5fa',
                    padding: '14px 28px',
                    borderRadius: '25px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    border: '2px solid rgba(59, 130, 246, 0.4)',
                    fontSize: '1rem',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.3)';
                    e.currentTarget.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                    e.currentTarget.style.transform = 'translateY(0)';
                  }}
                >
                  <FaComment size={14} />
                  تحدث معنا
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gap: '24px'
          }}>
            {orders.map((order, index) => {
              const formattedDate = formatDate(order.orderDate);
              const formattedTime = formatTime(order.orderDate);
              const statusInfo = getStatusInfo(order.status);
              const orderStats = calculateOrderStats(order);

              const isExpanded = expandedOrders.has(order.id);

              return (
                <div key={order.id} style={{
                  background: 'linear-gradient(135deg, rgba(30, 41, 59, 0.95), rgba(51, 65, 85, 0.95))',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${statusInfo.color}40`,
                  borderRadius: '24px',
                  padding: '32px',
                  boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px ${statusInfo.color}20`,
                  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: 'fadeInUp 0.5s ease-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.boxShadow = `0 30px 60px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px ${statusInfo.color}40`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = `0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px ${statusInfo.color}20`;
                }}>
                  {/* Gradient overlay */}
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '5px',
                    background: `linear-gradient(90deg, ${statusInfo.color}, ${statusInfo.color}80, ${statusInfo.color})`,
                    boxShadow: `0 0 20px ${statusInfo.color}40`
                  }}></div>

                  {/* Glowing corner accent */}
                  <div style={{
                    position: 'absolute',
                    top: '-50%',
                    right: '-50%',
                    width: '200%',
                    height: '200%',
                    background: `radial-gradient(circle, ${statusInfo.color}10 0%, transparent 70%)`,
                    pointerEvents: 'none'
                  }}></div>

                  {/* Order Header */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    marginBottom: '24px',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}>
                    <div>
                      <h2 style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: '#cbd5e1',
                        margin: '0 0 8px 0',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px'
                      }}>
                        <span style={{
                          background: 'linear-gradient(135deg, #10b981, #06b6d4)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text'
                        }}>
                          الطلب رقم #{index + 1}
                        </span>
                      </h2>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '16px',
                        flexWrap: 'wrap'
                      }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: '#94a3b8',
                          fontSize: '0.9rem'
                        }}>
                          <FaCalendarAlt size={14} />
                          <span>{formattedDate}</span>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: '#94a3b8',
                          fontSize: '0.9rem'
                        }}>
                          <FaClock size={14} />
                          <span>{formattedTime}</span>
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      background: statusInfo.bgColor,
                      color: statusInfo.color,
                      padding: '8px 16px',
                      borderRadius: '20px',
                      border: `1px solid ${statusInfo.color}40`,
                      fontSize: '0.9rem',
                      fontWeight: '600'
                    }}>
                      {statusInfo.icon}
                      <span>{statusInfo.text}</span>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div style={{
                    display: 'flex',
                    gap: '12px',
                    marginBottom: '24px',
                    flexWrap: 'wrap'
                  }}>
                    <button
                      onClick={() => navigate('/chat')}
                      style={{
                        background: 'rgba(59, 130, 246, 0.1)',
                        border: '1px solid rgba(59, 130, 246, 0.3)',
                        color: '#60a5fa',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.2)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(59, 130, 246, 0.1)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <FaComment size={12} />
                      سؤال حول الطلب
                    </button>
                    <a
                      href="tel:+213781219669"
                      style={{
                        background: 'rgba(16, 185, 129, 0.1)',
                        border: '1px solid rgba(16, 185, 129, 0.3)',
                        color: '#10b981',
                        padding: '8px 16px',
                        borderRadius: '12px',
                        fontSize: '0.85rem',
                        fontWeight: '500',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        textDecoration: 'none',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.2)';
                        e.currentTarget.style.transform = 'scale(1.05)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(16, 185, 129, 0.1)';
                        e.currentTarget.style.transform = 'scale(1)';
                      }}
                    >
                      <FaPhone size={12} />
                      اتصل بنا
                    </a>
                    {order.items && order.items.length > 1 && (
                      <button
                        onClick={() => toggleOrderExpansion(order.id)}
                        style={{
                          background: 'rgba(139, 92, 246, 0.1)',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          color: '#a78bfa',
                          padding: '8px 16px',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(139, 92, 246, 0.2)';
                          e.currentTarget.style.transform = 'scale(1.05)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(139, 92, 246, 0.1)';
                          e.currentTarget.style.transform = 'scale(1)';
                        }}
                      >
                        {isExpanded ? 'إخفاء' : 'عرض'} التفاصيل
                        <FaChevronRight size={10} style={{
                          transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease'
                        }} />
                      </button>
                    )}
                  </div>

                  {/* Order Items */}
                  <div style={{
                    maxHeight: isExpanded ? 'none' : '400px',
                    overflow: isExpanded ? 'visible' : 'hidden',
                    transition: 'max-height 0.4s ease',
                    marginBottom: '24px'
                  }}>
                    {renderOrderItems(order)}
                  </div>

                  {/* Order Timeline */}
                  {order.status !== 'CancelledByClient' && renderOrderTimeline(order)}

                  {/* Price and Actions */}
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: '16px'
                  }}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ color: '#94a3b8', fontSize: '1rem' }}>الإجمالي:</span>
                      <span style={{
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                      }}>
                        {order.totalPrice} DZD
                      </span>
                      {orderStats.hasDiscount && (
                        <span style={{
                          color: '#10b981',
                          fontSize: '0.9rem',
                          textDecoration: 'line-through',
                          marginRight: '8px'
                        }}>
                          {orderStats.originalPrice} DZD
                        </span>
                      )}
                    </div>

                    <div style={{
                      display: 'flex',
                      gap: '12px',
                      flexWrap: 'wrap'
                    }}>
                      {order.status === 'Pending' && (
                        <button
                          style={{
                            background: cancellingOrder === order.id 
                              ? 'rgba(107, 114, 128, 0.5)' 
                              : 'linear-gradient(135deg, #ef4444, #dc2626)',
                            color: 'white',
                            padding: '12px 24px',
                            border: 'none',
                            borderRadius: '16px',
                            cursor: cancellingOrder === order.id ? 'not-allowed' : 'pointer',
                            fontSize: '0.9rem',
                            fontWeight: '600',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            boxShadow: '0 10px 25px -5px rgba(239, 68, 68, 0.3)'
                          }}
                          disabled={cancellingOrder === order.id}
                          onClick={() => {
                            if (window.confirm('هل أنت متأكد أنك تريد إلغاء هذا الطلب؟ 🤔\n\nلن تتمكن من التراجع عن هذا الإجراء.')) {
                              cancelOrder(order.id);
                            }
                          }}
                          onMouseEnter={(e) => {
                            if (cancellingOrder !== order.id) {
                              e.target.style.transform = 'translateY(-2px)';
                              e.target.style.boxShadow = '0 15px 35px -5px rgba(239, 68, 68, 0.4)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (cancellingOrder !== order.id) {
                              e.target.style.transform = 'translateY(0)';
                              e.target.style.boxShadow = '0 10px 25px -5px rgba(239, 68, 68, 0.3)';
                            }
                          }}
                        >
                          {cancellingOrder === order.id ? (
                            <>
                              <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                              جاري الإلغاء...
                            </>
                          ) : (
                            <>
                              <FaTimes />
                              إلغاء الطلب
                            </>
                          )}
                        </button>
                      )}
                      {orderStats.hasDiscount && (
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '8px 16px',
                          background: 'rgba(16, 185, 129, 0.1)',
                          border: '1px solid rgba(16, 185, 129, 0.3)',
                          borderRadius: '16px',
                          color: '#10b981',
                          fontSize: '0.85rem',
                          fontWeight: '600'
                        }}>
                          <FaTags />
                          خصم 10% مطبق
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes gradientShift {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes float {
          0%, 100% { transform: translateY(0px) translateX(0px); }
          33% { transform: translateY(-20px) translateX(10px); }
          66% { transform: translateY(10px) translateX(-10px); }
        }
        
        @keyframes fadeInDown {
          from {
            opacity: 0;
            transform: translateY(-30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @media (max-width: 768px) {
          .order-card {
            padding: 20px !important;
          }
          .order-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .order-stats {
            grid-template-columns: 1fr !important;
          }
          .order-footer {
            flex-direction: column !important;
            align-items: stretch !important;
            gap: 16px !important;
          }
          .order-timeline {
            font-size: 0.7rem !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OrdersPage;